import { createContext, useContext, useEffect, useState } from "react"
import { iFormsContext, iProvider, } from "../types";
import { Form, FormTemplate, } from "../types/schema";
import { authClient } from "../utils/amplifyClient";
import { useUserContext } from "./UserContext";
import { isInspectionFormsTabForm, resolveFormType } from "../utils/reportUtils";
import { FORM_TYPES } from "../utils/constants";
import { listFormByCompanyId } from "../graphql/queries";

export const FormsContext = createContext<iFormsContext>({
    allForms: [],
    companyForms: [],
    formTemplates: [],
    companyDailyServiceForms: [],
    companyInspectionForms: [],
    companyPMForms: [],
    loading: false,
    refreshForms: async () => { },
    resetForms: () => { },
    updateFormInDatabase: async () => { },
});

// eslint-disable-next-line react-refresh/only-export-components
export const useFormsContext = () => useContext(FormsContext);

export const FormsProvider = ({ children }: iProvider) => {

    const { companyUser, companyOwner } = useUserContext();
    const [companyForms, setCompanyForms] = useState<Form[]>([]);
    const [formTemplates, setFormTemplates] = useState<FormTemplate[]>([]);
    const [companyDailyServiceForms, setCompanyDailyServiceForms] = useState<Form[]>([]);
    const [companyInspectionForms, setCompanyInspectionForms] = useState<Form[]>([]);
    const [companyPMForms, setCompanyPMForms] = useState<Form[]>([]);
    const [allForms, setAllForms] = useState<Form[]>([]);
    const [loading, setLoading] = useState(false);

    const companyId = (companyUser?.companyId as string) || companyOwner?.companyId;

    useEffect(() => {
        if (companyId) {
            refreshForms();
        } else {
            resetForms();
        }
    }, [companyId]);

    const refreshForms = async (): Promise<void> => {
        if (!companyId) return;

        setLoading(true);
        try {
            // Prefer GraphQL list so status/equipmentType/umCounterLabel are returned even when
            // local amplify_outputs model_introspection is missing those Form fields.
            const [companyFormsResponse, allFormsResult, allFormTemplates] = await Promise.all([
                authClient.graphql({
                    query: listFormByCompanyId,
                    variables: { companyId },
                }) as Promise<{
                    data?: { listFormByCompanyId?: { items?: Form[] | null } | null };
                }>,
                authClient.models.Form.list(),
                authClient.models.FormTemplate.list(),
            ]);

            const templates = allFormTemplates.data as FormTemplate[];
            const allFormsList = allFormsResult.data as Form[];
            const companyFormsList =
                (companyFormsResponse.data?.listFormByCompanyId?.items?.filter(Boolean) as Form[]) || [];

            setAllForms(allFormsList);
            setCompanyForms(companyFormsList);
            setFormTemplates(templates);
            setCompanyDailyServiceForms(companyFormsList.filter(form => resolveFormType(form) === FORM_TYPES.DAILY_SERVICE));
            setCompanyInspectionForms(companyFormsList.filter(isInspectionFormsTabForm));
            setCompanyPMForms(companyFormsList.filter(form => resolveFormType(form) === FORM_TYPES.PM_SERVICE));

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const resetForms = (): void => {
        setAllForms([]);
        setCompanyForms([]);
        setFormTemplates([]);
        setCompanyDailyServiceForms([]);
        setCompanyInspectionForms([]);
        setCompanyPMForms([]);
        setLoading(false);
    };

    const updateFormInDatabase = async (formId: string, updates: Partial<Form>) => {
        try {
            const { createdAt, updatedAt, owner, ...cleanUpdates } = updates as Record<string, unknown>;
            void createdAt; void updatedAt; void owner;

            await authClient.models.Form.update({
                id: formId,
                ...cleanUpdates,
            });
        } catch (error) {
            console.error(`Error updating formId ${formId}:`, error);
            throw error;
        }
    };

    const value: iFormsContext = {
        allForms,
        companyForms,
        formTemplates,
        companyDailyServiceForms,
        companyInspectionForms,
        companyPMForms,
        loading,
        refreshForms,
        resetForms,
        updateFormInDatabase,
    };

    return (
        <FormsContext.Provider value={value}>{children}</FormsContext.Provider>
    );
};
